import type { Position } from '../../nodes/InlineImageNode/InlineImageNode';
import type { JSX } from 'react';

import '../../nodes/InlineImageNode/InlineImageNode.css';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $createRangeSelection,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  isHTMLElement,
  LexicalCommand,
  LexicalEditor,
} from 'lexical';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import {
  $createInlineImageNode,
  $isInlineImageNode,
  InlineImageNode,
  InlineImagePayload,
} from '../../nodes/InlineImageNode/InlineImageNode';
import { DialogActions } from '../../ui/Dialog';
import useManageAttachment from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageAttachment';
import { useParams } from 'react-router-dom';
import {
  MenuItem,
  TextField,
  Select,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  FormControl,
  InputLabel,
} from '@mui/material';
import FileUploader, { FileInfo } from 'components/_atoms/FileUploader';
import { useQueryClient } from 'react-query';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';

export type InsertInlineImagePayload = Readonly<InlineImagePayload>;

export const INSERT_INLINE_IMAGE_COMMAND: LexicalCommand<InlineImagePayload> =
  createCommand('INSERT_INLINE_IMAGE_COMMAND');

function base64ToBlob(base64: string, mime: string): Blob {
  base64 = base64.replace(/\s/g, '');
  const binary = atob(base64);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    array[i] = binary.charCodeAt(i);
  }
  return new Blob([array], { type: mime });
}

async function uploadFile(presignedUrl: string, file: File) {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type,
    },
  });
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`S3 Upload Failed | Status: ${response.status} | Error: ${errorText}`);
    throw new Error(`S3 upload failed: ${response.statusText} | Details: ${errorText}`);
  }
}

export function InsertInlineImageDialog({
  activeEditor,
  onClose,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
}): JSX.Element {
  // Now we use FileUploader's FileInfo rather than a raw File.
  const [fileInfo, setFileInfo] = useState<FileInfo | null>(null);
  const [altText, setAltText] = useState('');
  const [position, setPosition] = useState<Position>('left');
  const [showCaption, setShowCaption] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const hasModifier = useRef(false);
  const { id: routeManualId } = useParams();
  const manualId = routeManualId ?? 'manualId';

  const { createAttachment, getAttachment } = useManageAttachment();
  const { getLock, removeLock } = useManageManual();
  const queryClient = useQueryClient();

  // For tracking modifier keys (if needed in the future)
  useEffect(() => {
    hasModifier.current = false;
    const handler = (e: KeyboardEvent) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [activeEditor]);

  const handleFileChange = (info: FileInfo) => {
    setFileInfo(info);
  };

  const handlePositionChange = (value: string) => {
    setPosition(value as Position);
  };

  const handleShowCaptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowCaption(e.target.checked);
  };

  const handleConfirm = async () => {
    if (!fileInfo || !manualId) {
      console.error('No file selected or invalid manualId');
      return;
    }
    setIsUploading(true);

    try {
      await new Promise<void>((resolve, reject) => {
        getLock(
          manualId,
          () => {
            resolve();
          },
          (err) => {
            console.error('Failed to lock manual:', err);
            reject(err);
          }
        );
      });

      let uploadUrl: string | null = null;
      let attachmentId: string | null = null;

      await new Promise<void>((resolve, reject) => {
        // Create an attachment using the file info.
        createAttachment(
          {
            manualId,
            file: new File([fileInfo.data], fileInfo.name, { type: fileInfo.type }),
            name: fileInfo.name,
            size: fileInfo.size,
            globalAttachmentId: '',
            jurisdictionAttachmentId: '',
          },
          (url, id) => {
            uploadUrl = url;
            attachmentId = id;
            resolve();
          },
          (error) => {
            console.error('Error creating attachment:', error);
            reject(error);
          }
        );
      });

      if (!uploadUrl || !attachmentId) {
        throw new Error('Presigned URL or attachment ID is missing');
      }

      const blob = base64ToBlob(fileInfo.data, fileInfo.type);
      const file = new File([blob], fileInfo.name, { type: fileInfo.type });
      await uploadFile(uploadUrl, file);
      const attachment = await getAttachment({ manualId, attachmentId });
      const payload: InsertInlineImagePayload = {
        altText,
        position,
        showCaption,
        src: attachment.url,
      };
      activeEditor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, payload);
    } catch (err) {
      console.error('Error in lock/create/upload flow:', err);
    } finally {
      await new Promise<void>((resolve, reject) => {
        removeLock(
          manualId,
          () => {
            queryClient.invalidateQueries(['draft_manual', manualId]);
            resolve();
          },
          (error) => {
            console.error('Failed to remove lock:', error);
            reject(error);
          }
        );
      });
      setIsUploading(false);
      onClose();
    }
  };

  const isDisabled = !fileInfo || isUploading;

  return (
    <>
      <Box sx={{ marginBottom: '1em' }}>
        <FileUploader
          onChange={handleFileChange}
          onError={(errors) => {
            console.error('File upload errors:', errors);
          }}
          placeholder='Drag or click to select an image'
          data-test-id='inline-image-file-uploader'
        />
      </Box>
      <Box sx={{ marginBottom: '1em' }}>
        <TextField
          label='Alt Text'
          placeholder='Descriptive alternative text'
          value={altText}
          onChange={(e) => setAltText(e.target.value)}
          fullWidth
          margin='normal'
          inputProps={{ 'data-test-id': 'inline-image-alt-text-input' }}
        />
      </Box>
      <FormControl sx={{ m: 1, minWidth: 120 }}>
        <InputLabel id='inline-image-position-select-label'>Position</InputLabel>
        <Select
          labelId='inline-image-position-select-label'
          value={position}
          onChange={(e) => handlePositionChange(e.target.value)}
          fullWidth
          displayEmpty
          inputProps={{ 'data-test-id': 'inline-image-position-select' }}
        >
          <MenuItem value='left'>Left</MenuItem>
          <MenuItem value='right'>Right</MenuItem>
          <MenuItem value='full'>Full Width</MenuItem>
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Checkbox
            checked={showCaption}
            onChange={handleShowCaptionChange}
          />
        }
        label='Show Caption'
        data-test-id='inline-image-caption-checkbox'
      />
      <DialogActions>
        <Button
          data-test-id='inline-image-confirm-btn'
          disabled={isDisabled}
          onClick={handleConfirm}
        >
          {isUploading ? 'Uploading...' : 'Confirm'}
        </Button>
      </DialogActions>
    </>
  );
}

export default function InlineImagePlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([InlineImageNode])) {
      throw new Error('ImagesPlugin: ImageNode not registered on editor');
    }

    return mergeRegister(
      editor.registerCommand<InsertInlineImagePayload>(
        INSERT_INLINE_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createInlineImageNode(payload);
          $insertNodes([imageNode]);
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand<DragEvent>(
        DRAGSTART_COMMAND,
        (event) => {
          return $onDragStart(event);
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand<DragEvent>(
        DRAGOVER_COMMAND,
        (event) => {
          return $onDragover(event);
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand<DragEvent>(
        DROP_COMMAND,
        (event) => {
          return $onDrop(event, editor);
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor]);

  return null;
}

const TRANSPARENT_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const img = document.createElement('img');
img.src = TRANSPARENT_IMAGE;

function $onDragStart(event: DragEvent): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  dataTransfer.setData('text/plain', '_');
  dataTransfer.setDragImage(img, 0, 0);
  dataTransfer.setData(
    'application/x-lexical-drag',
    JSON.stringify({
      data: {
        altText: node.__altText,
        caption: node.__caption,
        height: node.__height,
        key: node.getKey(),
        showCaption: node.__showCaption,
        src: node.__src,
        width: node.__width,
      },
      type: 'image',
    })
  );

  return true;
}

function $onDragover(event: DragEvent): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  if (!canDropImage(event)) {
    event.preventDefault();
  }
  return true;
}

function $onDrop(event: DragEvent, editor: LexicalEditor): boolean {
  const node = $getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const data = getDragImageData(event);
  if (!data) {
    return false;
  }
  event.preventDefault();
  if (canDropImage(event)) {
    const range = getDragSelection(event);
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);
    editor.dispatchCommand(INSERT_INLINE_IMAGE_COMMAND, data);
  }
  return true;
}

function $getImageNodeInSelection(): InlineImageNode | null {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) {
    return null;
  }
  const nodes = selection.getNodes();
  const node = nodes[0];
  return $isInlineImageNode(node) ? node : null;
}

function getDragImageData(event: DragEvent): null | InsertInlineImagePayload {
  const dragData = event.dataTransfer?.getData('application/x-lexical-drag');
  if (!dragData) {
    return null;
  }
  const { type, data } = JSON.parse(dragData);
  if (type !== 'image') {
    return null;
  }

  return data;
}

declare global {
  interface DragEvent {
    rangeOffset?: number;
    rangeParent?: Node;
  }
}

function canDropImage(event: DragEvent): boolean {
  const { target } = event;
  // If target is null or not an element, bail out
  if (!target || !(target instanceof HTMLElement)) {
    return false;
  }

  return !!(
    // isHTMLElement(target) is basically checking (target instanceof HTMLElement)
    (
      !target.closest('code, span.editor-image') &&
      target.parentElement &&
      target.parentElement.closest('div.ContentEditable__root')
    )
  );
}

function getDragSelection(event: DragEvent): Range | null {
  let range: Range | null = null;
  const domSelection = window.getSelection();
  // or document.getSelection(), either is fine

  if (!domSelection) {
    return null;
  }

  // For older browsers that support caretRangeFromPoint:
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (event.rangeParent) {
    domSelection.collapse(event.rangeParent, event.rangeOffset ?? 0);
    range = domSelection.rangeCount > 0 ? domSelection.getRangeAt(0) : null;
  } else {
    throw Error('Cannot get the selection when dragging');
  }

  return range;
}
